import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { useCurrentUser } from "../lib/useCurrentUser";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge, urgencyTone } from "../components/ui/Badge";
import { DataState } from "../components/ui/DataState";
import { reportTypeLabel, type Report } from "../lib/types";

export default function OrganizationReports() {
  const api = useApi();
  const { user } = useCurrentUser();
  const [reports, setReports] = useState<Report[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ reports: Report[] }>("/api/organization-admin/reports")
      .then((r) => {
        setReports(r.reports);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (user && user.organization_id === null) {
    return (
      <div className="container-page py-12">
        <div className="surface mx-auto max-w-xl p-8 text-center">
          <h1 className="text-2xl font-semibold text-white">
            No organization linked
          </h1>
          <p className="mt-3 text-slate-300">
            Your account is not yet associated with an organization. Ask a
            platform admin to assign your organization on the user-management
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-12">
      <header>
        <h1 className="text-3xl font-semibold text-white">
          Reports for your organization
        </h1>
        <p className="mt-2 max-w-3xl text-slate-300">
          You can see reports tied to your organization's location or contact.
          Anonymous reports never expose the submitter, even to organization
          admins.
        </p>
      </header>

      <div className="mt-8">
        <DataState
          loading={loading}
          error={error}
          data={reports}
          empty={!!reports && reports.length === 0}
          loadingMessage="Loading reports for your organization…"
          emptyMessage="No reports tied to your organization yet."
        >
          {(items) => (
            <div className="grid gap-4">
              {items.map((r) => (
                <Card key={r.id}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={urgencyTone(r.urgency)}>{r.urgency}</Badge>
                    <Badge>{reportTypeLabel(r.report_type)}</Badge>
                    {r.neighborhood && <Badge>{r.neighborhood}</Badge>}
                    <Badge tone="warning">{r.status}</Badge>
                    {r.anonymous ? <Badge>anonymous</Badge> : null}
                  </div>
                  <CardTitle className="mt-3 text-lg">
                    {r.location ?? "Location not specified"}
                  </CardTitle>
                  <p className="mt-2 whitespace-pre-line text-sm text-slate-300">
                    {r.description}
                  </p>
                  <div className="mt-3 text-xs text-slate-400">
                    Submitted {r.created_at}
                    {!r.anonymous && r.submitter_name && (
                      <> · by {r.submitter_name}</>
                    )}
                    {r.anonymous ? (
                      <> · submitter identity hidden</>
                    ) : null}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}
